import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

const BackgroundDiv = ({
  backgroundImg,
  width,
  height,
  aspectRatio,
  className,
  children,
  style,
  id,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef();
  const [calculatedSize, setCalculatedSize] = useState({ 
    width: null, 
    height: null,
    ratio: null 
  });

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { rootMargin: '200px' });

    const currentRef = containerRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  useEffect(() => {
    if (!isVisible || !backgroundImg || aspectRatio) return;

    const img = new Image();
    img.src = backgroundImg;
    
    img.onload = () => {
      const naturalRatio = img.naturalWidth / img.naturalHeight;
      setCalculatedSize(prev => ({ ...prev, ratio: naturalRatio }));
    };

    img.onerror = () => console.error('Failed to load background image');
  }, [backgroundImg, aspectRatio, isVisible]);

  const getFinalSize = useCallback(() => {
    const finalRatio = aspectRatio || calculatedSize.ratio;
    const definedWidth = typeof width !== 'undefined';
    const definedHeight = typeof height !== 'undefined';

    if (definedWidth && definedHeight) return { width, height };
    if (definedWidth && finalRatio) return {
      width: typeof width === 'number' ? `${width}px` : width,
      height: `calc(${width} / ${finalRatio})`
    };
    if (definedHeight && finalRatio) return {
      width: `calc(${height} * ${finalRatio})`,
      height: typeof height === 'number' ? `${height}px` : height
    };

    return {
      width: typeof width === 'number' ? `${width}px` : width || '100%',
      height: typeof height === 'number' ? `${height}px` : height || '300px'
    };
  }, [width, height, aspectRatio, calculatedSize.ratio]);

  const containerStyle = {
    backgroundImage: isVisible ? `url(${backgroundImg})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    ...getFinalSize(),
    ...style
  };

  return (
    <div 
      ref={containerRef}
      className={`aspect-ratio-container ${className || ''}`}
      style={containerStyle}
      {...props}
      id={id}
    >
      {children}
    </div>
  );
};

BackgroundDiv.propTypes = {
  backgroundImg: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  aspectRatio: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  children: PropTypes.node
};

BackgroundDiv.defaultProps = {
  width: undefined,
  height: undefined,
  aspectRatio: null
};

export default BackgroundDiv;